<script setup>
import { computed } from 'vue'
import useDateTimeFormatter from '@/composables/useDateTimeFormatter.js'

/**
 * The date time formatter composable.
 */
const { formatDateTime } = useDateTimeFormatter()

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The given load.
     *
     * @type {Object}
     */
    load: {
        type: Object,
        required: true,
    },

    /**
     * The current location of the load.
     *
     * @type {Object}
     */
    value: {
        type: Object,
        required: true,
    },
})

/**
 * The schedule arrive at.
 *
 * @return {String}
 */
const actualArriveAt = computed(() => {
    const arriveAt = props.value?.actual?.arrive_at

    if (!arriveAt) {
        return '--'
    }

    return formatDateTime(arriveAt, 'M/D/YY @ HH:mm')
})

/**
 * The load's estimated time of arrival.
 *
 * @return {String}
 */
const etaProjectedArrival = computed(() => {
    const projectedArrival = props.load?.eta?.projected_arrival

    if (!projectedArrival) {
        return '--'
    }

    return formatDateTime(projectedArrival, 'M/D/YY @ HH:mm')
})

/**
 * The schedule arrive at.
 *
 * @return {String}
 */
const scheduledArriveAt = computed(() => {
    const arriveAt = props.value?.scheduled?.arrive_at

    if (!arriveAt) {
        return '--'
    }

    return formatDateTime(arriveAt, 'M/D/YY @ HH:mm')
})

/**
 * The stop title.
 *
 * @return {String}
 */
const title = computed(() => {
    const location = props.value?.location

    const name = location?.name || 'N/A'
    const city = location?.city || 'N/A'
    const state = location?.state || 'N/A'

    return `${name} - ${city}, ${state}`
})
</script>

<template>
    <div>
        <div class="flex">
            <div class="w-48">
                <div class="text-gray-500 text-sm leading-none">Scheduled</div>
                <div data-test="stop-condensed-scheduled-arrival">
                    {{ scheduledArriveAt }}
                </div>
            </div>
            <div class="w-48">
                <div class="text-gray-500 text-sm leading-none">Actual</div>
                <div data-test="stop-condensed-actual-arrival">
                    {{ actualArriveAt }}
                </div>
            </div>
            <div class="w-48">
                <div class="text-gray-500 text-sm leading-none">ETA</div>
                <div>{{ etaProjectedArrival }}</div>
            </div>
        </div>
        <div>
            <span class="text-sm" data-test="stop-condensed-title">
                {{ title }}
            </span>
        </div>
    </div>
</template>
